/**
 * The rail footer's account row, as a menu, drawn as finixt-ui's
 * AccountSwitcher.
 *
 * It is the one way to sign out of the app. Sign-out arrives as a capability
 * from above rather than by reaching for the realm session here; see
 * `auth/sign-out.tsx` for why that matters. Without it the item says so
 * rather than the shell crashing on a missing provider.
 */
import { forwardRef, useContext, useState, type ButtonHTMLAttributes } from 'react';

import type { Me } from '@optum/api-client/types';

import { useAuth } from '../../auth/AuthContext.js';
import { useSignOut } from '../../auth/sign-out.js';
import { Dropdown, DropdownDivider, DropdownItem } from '../../ui/Dropdown.js';
import { Avatar, Icon, ShellContext } from '../../ui/finixt.js';

const INVERSE = { tint: 'var(--surface-inverse)', color: 'var(--text-inverse)' };

/**
 * What the row calls the person: the username unless the realm made it the
 * address, otherwise the part of the address before the @. The rail has room
 * for about 120px on that line; the full address is in the tooltip and the
 * open menu.
 */
function displayName(user: Pick<Me, 'email' | 'username'> | null): string {
  if (user?.username && !user.username.includes('@')) return user.username;
  return user?.email.split('@')[0] || 'Operator';
}

/** Up to two letters for the avatar, from the first and last words. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** The trigger: avatar, name, role; the full address as its tooltip. */
interface TriggerProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  name: string;
  email: string;
  role: string;
  collapsed: boolean;
}

const Trigger = forwardRef<HTMLButtonElement, TriggerProps>(function Trigger(
  { name, email, role, collapsed, ...rest },
  ref,
) {
  const [hover, setHover] = useState(false);
  return (
    <button
      ref={ref}
      type="button"
      title={email || name}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        width: collapsed ? undefined : '100%',
        boxSizing: 'border-box',
        padding: collapsed ? 0 : '5px 7px',
        border: `1px solid ${!collapsed && hover ? 'var(--border-hairline)' : 'transparent'}`,
        borderRadius: 10,
        background: !collapsed && hover ? 'var(--surface-card)' : 'transparent',
        cursor: 'pointer',
        textAlign: 'left',
        fontFamily: 'var(--font-sans)',
      }}
      {...rest}
    >
      <Avatar initials={initials(name)} shape="square" size={26} {...INVERSE} />
      {collapsed ? null : (
        <>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span
              style={{
                display: 'block',
                fontSize: 14,
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--text-strong)',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {name}
            </span>
            <span
              style={{
                display: 'block',
                fontSize: 9.5,
                letterSpacing: '.06em',
                textTransform: 'uppercase',
                color: 'var(--text-muted)',
                marginTop: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
              className="mono"
            >
              {role}
            </span>
          </span>
          <Icon name="unfold_more" size={17} color="var(--text-soft)" style={{ flexShrink: 0 }} />
        </>
      )}
    </button>
  );
});

/** The rail footer's account row and its menu: who is signed in, and sign out. */
export function AccountMenu() {
  const { user } = useAuth();
  const signOut = useSignOut();
  const shell = useContext(ShellContext);
  const collapsed = Boolean(shell.railCollapsed);
  const name = displayName(user);
  const email = user?.email ?? '';
  const role = user?.platform_role?.replace('optum-platform-', '') ?? 'staff';

  return (
    <Dropdown
      width={282}
      side="top"
      align="start"
      trigger={<Trigger name={name} email={email} role={role} collapsed={collapsed} />}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '5px 9px 7px' }}>
        <Avatar initials={initials(name)} size={32} {...INVERSE} />
        <span style={{ minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-body-sm)',
              fontWeight: 'var(--weight-semibold)',
              color: 'var(--text-strong)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {name}
          </span>
          {/* Wraps rather than clips: the menu is the one place the whole address shows. */}
          {email ? (
            <span
              style={{
                display: 'block',
                fontSize: 'var(--text-body-sm)',
                color: 'var(--text-muted)',
                overflowWrap: 'anywhere',
              }}
            >
              {email}
            </span>
          ) : null}
          <span
            style={{
              display: 'block',
              fontSize: 'var(--text-meta)',
              textTransform: 'uppercase',
              letterSpacing: '.06em',
              color: 'var(--text-faint)',
              marginTop: 1,
            }}
            className="mono"
          >
            {role}
          </span>
        </span>
      </div>
      <DropdownDivider />
      <DropdownItem
        icon="logout"
        label={signOut ? 'Sign out' : 'Sign out unavailable'}
        tone="danger"
        disabled={!signOut}
        onSelect={() => void signOut?.()}
      />
    </Dropdown>
  );
}
